import * as React from "react";
import { Camera, Upload } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@/components/ui/button";
import { CameraCapture } from "@/components/ui/camera-capture";

/** Multi-file drag-and-drop picker, styled to match shadcn/ui's other
 *  primitives (no official shadcn "Dropzone" component exists in the base
 *  registry - this follows the same pattern their docs use elsewhere: a
 *  hidden native <input type="file"> plus drag event handlers). */
export interface DropzoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  className?: string;
  children?: React.ReactNode;
  /** Rejects (and reports via onFilesRejected, without ever reaching
   *  onFilesSelected) any file over this size - this app's own expense/bill
   *  upload enforces 50MB (see ExpensesStep.tsx's MAX_BILL_FILE_BYTES),
   *  matching the backend's Flask MAX_CONTENT_LENGTH so a file that would
   *  be accepted here is never later bounced by the server instead.
   *  Unset = no client-side limit (the caller/server still may have one). */
  maxSizeBytes?: number;
  /** Files filtered out by maxSizeBytes OR by not matching `accept`,
   *  reported separately so the caller can surface a message - Dropzone
   *  itself has no toast/error UI of its own. Not called for a drop/pick
   *  with nothing rejected. Needed because `accept` is NOT enough on its
   *  own: the browser only applies it to the native file-picker dialog
   *  (pick()) - a drag-and-drop onDrop bypasses it completely, so without
   *  this a .docx/.pptx/etc. dropped onto the box was silently accepted
   *  even though the picker dialog would have hidden it. */
  onFilesRejected?: (files: File[]) => void;
  /** Adds a separate "Take photo" button, phone-only, that opens a
   *  continuous in-app camera session (CameraCapture) instead of the normal
   *  file/gallery picker - lets someone shoot several bill photos back to
   *  back without the camera closing between shots, for photographing paper
   *  receipts on the spot rather than picking existing files. Falls back
   *  to the native <input capture> single-shot behaviour, transparently,
   *  whenever getUserMedia can't run (no API, permission denied, no camera,
   *  etc.) - that native input exists as a SEPARATE hidden input from the
   *  main one below purely so this fallback stays available without ever
   *  touching the main click-anywhere/drag area's own plain picker. Desktop
   *  browsers have no camera-vs-gallery distinction, so it's phone-only. */
  allowCameraCapture?: boolean;
}

const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  (
    {
      onFilesSelected,
      accept,
      multiple = true,
      className,
      children,
      maxSizeBytes,
      onFilesRejected,
      allowCameraCapture
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const cameraInputRef = React.useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [cameraStream, setCameraStream] = React.useState<MediaStream | null>(
      null
    );
    const isPhone = useMediaQuery(PHONE_QUERY);

    const pick = () => inputRef.current?.click();
    const pickFromCamera = () => cameraInputRef.current?.click();

    // A phone's native camera APP drives continuous autofocus/auto-exposure
    // itself; a plain getUserMedia() call does NOT ask for that explicitly,
    // which is one real, documented source of a web capture looking softer
    // than the same phone's own camera app (the lens may settle on whatever
    // it last focused at, or hunt less aggressively than the native stack
    // does). `focusMode`/`exposureMode: "continuous"` are Chrome-for-Android-
    // only (no support on desktop Chrome or iOS Safari per every source
    // found) and MUST stay best-effort: checked against the track's own
    // getCapabilities() first (applyConstraints with a property the device
    // doesn't advertise can throw depending on the implementation), and
    // never awaited/surfaced to the caller - a camera that opens with
    // default focus/exposure is still strictly better than one that failed
    // to open at all over a non-essential enhancement.
    const lockContinuousFocusAndExposure = async (stream: MediaStream) => {
      const [track] = stream.getVideoTracks();
      if (!track || typeof track.getCapabilities !== "function") return;
      try {
        const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
          focusMode?: string[];
          exposureMode?: string[];
        };
        const advanced: MediaTrackConstraintSet[] = [];
        if (capabilities.focusMode?.includes("continuous")) {
          advanced.push({ focusMode: "continuous" } as MediaTrackConstraintSet);
        }
        if (capabilities.exposureMode?.includes("continuous")) {
          advanced.push({
            exposureMode: "continuous"
          } as MediaTrackConstraintSet);
        }
        if (advanced.length > 0) {
          await track.applyConstraints({ advanced });
        }
      } catch {
        // Best-effort only - an unsupported or rejected constraint here
        // just means the device keeps whatever focus/exposure it already
        // had, not a broken camera.
      }
    };

    // Tries the continuous in-app camera session first; falls back to the
    // native single-shot <input capture> transparently whenever
    // getUserMedia can't run. The pre-flight check alone also covers the
    // insecure-context case (the API simply doesn't exist on a non-HTTPS,
    // non-localhost origin), so there's nothing further to check there.
    const openCamera = async () => {
      if (
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== "function"
      ) {
        pickFromCamera();
        return;
      }
      try {
        // Deliberately NO width/height hint here - requesting a high-res
        // stream for the live VIEWFINDER made the preview noticeably
        // laggier (continuously decoding/rendering a large frame every tick
        // costs far more than taking one still). The actual still-capture
        // resolution is handled separately in CameraCapture's capturePhoto,
        // via ImageCapture.takePhoto() pulling a full-sensor frame
        // independent of this preview stream's own resolution wherever the
        // browser supports it.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false
        });
        void lockContinuousFocusAndExposure(stream);
        setCameraStream(stream);
      } catch {
        // Permission denied, no camera, camera in use elsewhere, requested
        // facingMode unsatisfiable, etc. - a toast here (not for the
        // pre-flight branch above, which is silent) because the browser
        // may not re-prompt on a later attempt, so the user should know
        // something happened rather than wonder why nothing opened.
        toast.info("Using your camera app instead.");
        pickFromCamera();
      }
    };

    // Same comma-separated syntax the `accept` attribute itself takes:
    // a MIME type ("application/pdf"), a MIME wildcard ("image/*"), or a
    // file extension (".pdf"). Mirrors what the native file-picker dialog
    // already enforces on `pick()` - needed here too since onDrop never
    // goes through that dialog at all (see onFilesRejected's doc comment).
    const matchesAccept = (file: File): boolean => {
      if (!accept) return true;
      const patterns = accept
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      if (patterns.length === 0) return true;
      const name = file.name.toLowerCase();
      const type = file.type.toLowerCase();
      return patterns.some((pattern) => {
        const p = pattern.toLowerCase();
        if (p.startsWith(".")) return name.endsWith(p);
        if (p.endsWith("/*")) return type.startsWith(p.slice(0, -1));
        return type === p;
      });
    };

    // Accepts a FileList (the native inputs/drag-drop below) or a plain
    // File[] (CameraCapture's onCapture output) identically - Array.from
    // produces an equivalent array either way, so no branching is needed;
    // this just routes every source through the same matchesAccept/
    // maxSizeBytes filtering rather than CameraCapture needing its own copy.
    const handleFiles = (fileList: FileList | File[] | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);
      const accepted = files.filter(
        (f) => f.size <= (maxSizeBytes ?? Infinity) && matchesAccept(f)
      );
      const rejected = files.filter(
        (f) => f.size > (maxSizeBytes ?? Infinity) || !matchesAccept(f)
      );
      if (rejected.length > 0) onFilesRejected?.(rejected);
      if (accepted.length > 0) onFilesSelected(accepted);
    };

    return (
      <>
        <div
          ref={ref}
          role="button"
          tabIndex={0}
          onClick={pick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              pick();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-input hover:bg-accent/50",
            className
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              // Allow re-selecting the same file(s) again later.
              e.target.value = "";
            }}
          />
          {allowCameraCapture && isPhone && (
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          )}
          {children ?? (
            <>
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">
                Drag and drop files here, or click to choose
              </p>
              <p className="text-xs text-muted-foreground">
                Images or PDF, multiple files allowed
                {maxSizeBytes != null &&
                  ` - up to ${Math.round(maxSizeBytes / (1024 * 1024))}MB each`}
              </p>
            </>
          )}
          {allowCameraCapture && isPhone && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="relative z-10"
              onClick={(e) => {
                // Stop this bubbling to the outer box's own onClick, which
                // would also fire pick() (the plain file/gallery picker) at
                // the same time as this button's openCamera().
                e.stopPropagation();
                void openCamera();
              }}
            >
              <Camera className="mr-1.5 h-3.5 w-3.5" /> Take photo
            </Button>
          )}
        </div>
        {cameraStream && (
          <CameraCapture
            stream={cameraStream}
            onClose={() => setCameraStream(null)}
            onCapture={(files) => {
              setCameraStream(null);
              handleFiles(files);
            }}
          />
        )}
      </>
    );
  }
);
Dropzone.displayName = "Dropzone";

export { Dropzone };
